import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import MediaUploader from '../common/MediaUploader';

interface User {
    id?: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    email: string;
    role: string[] | string;
    active?: boolean;
    image_id?: string | number;
    [key: string]: any;
}

interface UserEditModalProps {
    user: User | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function UserEditModal({ user, isOpen, onClose, onSave }: UserEditModalProps) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState<string[]>(['customer']);
    const [isActive, setIsActive] = useState(true);
    const [userGroup, setUserGroup] = useState<number | ''>('');
    const [imageId, setImageId] = useState<number | ''>('');

    const [isSaving, setIsSaving] = useState(false);

    // Update state when user changes (Edit Mode) or resets (Create Mode)
    useEffect(() => {
        if (isOpen) {
            if (user) {
                setFirstName(user.first_name || '');
                setLastName(user.last_name || '');
                setUsername(user.username || '');
                setEmail(user.email || '');
                // Handle role mapping
                if (Array.isArray(user.role)) {
                    setRoles(user.role);
                } else if (typeof user.role === 'string') {
                    setRoles([user.role.toLowerCase()]);
                } else {
                    setRoles(['customer']);
                }
                setIsActive(user.active !== false);
                setUserGroup(user.user_group || '');
                setImageId(user.image_id ? Number(user.image_id) : '');
            } else {
                // Reset for create
                setFirstName('');
                setLastName('');
                setUsername('');
                setEmail('');
                setRoles(['customer']);
                setIsActive(true);
                setUserGroup('');
                setImageId('');
            }
        }
    }, [user, isOpen]);

    const handleSave = async () => {
        // Validation
        if (!firstName || !lastName || !email) {
            Swal.fire({
                icon: 'error',
                title: 'Required Fields',
                text: 'Please fill in all required fields (*).',
            });
            return;
        }

        // Username is required for create mode
        if (!user && !username) {
            Swal.fire({
                icon: 'error',
                title: 'Required Field',
                text: 'Username is required when creating a new user.',
            });
            return;
        }

        // Role validation
        if (roles.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Required Field',
                text: 'Please select at least one role.',
            });
            return;
        }

        setIsSaving(true);

        try {
            if (user && user.id) {
                // Update
                const updatePayload: any = {
                    id: user.id,
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    role: roles,
                    active: isActive,
                };

                if (userGroup) updatePayload.user_group = Number(userGroup);
                if (imageId) updatePayload.image_id = Number(imageId);

                await (client as any).postApiAccountsUserUpdateId({
                    id: user.id.toString(),
                    postApiAccountsUserUpdateIdRequest: updatePayload
                });

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'User updated successfully!',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
            } else {
                // Create
                const createPayload: any = {
                    first_name: firstName,
                    last_name: lastName,
                    username: username,
                    email: email,
                    role: roles,
                    active: isActive,
                };

                if (userGroup) createPayload.user_group = Number(userGroup);
                if (imageId) createPayload.image_id = Number(imageId);

                await (client as any).postApiAccountsUserCreate({
                    postApiAccountsUserCreateRequest: createPayload
                });
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'User created successfully!',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });

            }

            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to save user:', err);
            // Handle backend JSON error structure
            let errorMessage = 'Failed to save user. Please try again.';
            if (err?.response?.data) {
                const data = err.response.data;
                if (data.error) {
                    errorMessage = data.error;
                } else if (data.message) {
                    errorMessage = data.message;
                    // Check for validation errors if it's a JSON object
                    if (data.errors) {
                        errorMessage = Object.values(data.errors).flat().join('\n');
                    }
                }
            }

            Swal.fire({
                icon: 'error',
                title: 'Save Error',
                text: errorMessage,
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Fixed Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                        {user ? 'Edit User' : 'Add New User'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 overflow-y-auto flex-1 custom-scrollbar max-h-[60vh]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                                placeholder="John"
                            />
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                                placeholder="Doe"
                            />
                        </div>

                        {/* Username */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Username <span className="text-red-500">*</span>
                                {user && <span className="text-xs text-gray-500 ml-2">(read-only)</span>}
                            </label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={!!user}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                placeholder="johndoe"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                                placeholder="john@example.com"
                            />
                        </div>

                        {/* Roles */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Roles <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-wrap gap-3">
                                {['admin', 'customer'].map((role) => (
                                    <label key={role} className="flex items-center space-x-2 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={roles.includes(role)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setRoles([...roles, role]);
                                                } else {
                                                    setRoles(roles.filter(r => r !== role));
                                                }
                                            }}
                                            className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 transition-colors"
                                        />
                                        <span className="text-gray-700 dark:text-gray-300 capitalize group-hover:text-brand-500 transition-colors">{role}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* User Group (Optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                User Group <span className="text-xs text-gray-500">(Optional)</span>
                            </label>
                            <input
                                type="number"
                                value={userGroup}
                                onChange={(e) => setUserGroup(e.target.value ? Number(e.target.value) : '')}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                                placeholder="Enter user group ID"
                            />
                        </div>

                        {/* Image Uploader */}
                        <div className="md:col-span-2">
                            <MediaUploader
                                initialImages={imageId ? [Number(imageId)] : []}
                                multiple={false}
                                onUpload={(ids) => setImageId(ids.length > 0 ? ids[0] : '')}
                                label="Profile Image"
                                folder="users"
                            />
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {imageId ? `Selected Image ID: ${imageId}` : 'No image selected'}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Account Status <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={isActive ? 'Active' : 'Inactive'}
                                onChange={(e) => setIsActive(e.target.value === 'Active')}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-6 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-6 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 shadow-sm shadow-brand-500/20 disabled:opacity-50 transition-all hover:shadow-md hover:shadow-brand-500/30 flex items-center gap-2"
                    >
                        {isSaving ? (
                            <>
                                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (user ? 'Update User' : 'Create User')}
                    </button>
                </div>
            </div>
        </div>
    );
}
